import axios from 'axios';
import { kebabCase } from 'lodash';
import { v4 as uuid } from 'uuid';
import { get, getJSON, set } from 'js-cookie';
import config from 'config/config';
import { isUserBot } from 'src/utils/seo';
import { isCookieConsentGiven, getBranch, CookieCategory, Cookie } from 'src/utils/cookie';
import log from 'loglevel';
import EventType, { ANONYMOUS_EVENTS } from 'src/constants/footfallEventTypes';

const isServer = typeof window === 'undefined';
const isProduction = config.environment === 'production';
let membershipId: string | null = null;
let deviceId: string | null = null;

type Data = { [key: string]: any };

export async function trackFootfallEvent(eventType: EventType, data: Data = {}) {
  if (isServer || isUserBot) return;

  const isAnonymous = !isCookieConsentGiven(CookieCategory.Analytics);
  if (isAnonymous && !ANONYMOUS_EVENTS.includes(eventType)) return;

  await setMembershipAndDeviceIds(isAnonymous);

  if (!isValidEvent(eventType, data)) {
    return log.info('Footfall data not valid', eventType);
  }

  const extendedData = {
    'device-id': deviceId,
    'event-type': eventType,
    'membership-id': membershipId,
    'referrer': getReferrer(isAnonymous),
    'session-id': getSessionId(isAnonymous),
    'source': getSource(),
    'url': `${window.location.pathname}${window.location.search}`.replace(/\s/g, '%20'), // replace whitespace in URL
    ...convertKeysToKebabCase(data),
  };

  if (config.environment === 'development') {
    console.info('Footfall event:', extendedData); // eslint-disable-line no-console
  } else if (!isProduction) {
    await axios.post(
      'https://hu-staging.be/footfall',
      JSON.stringify(extendedData),
      { headers: { 'Accept': 'application/json' } },
    ).catch(err => log.info(err, 'Error posting to Footfall'));
  } else {
    await axios.post(
      'https://healthunlocked.com/footfall',
      JSON.stringify(extendedData),
      { headers: { 'Accept': 'application/json' } },
    ).catch(err => log.info(err, 'Error posting to Footfall'));
  }
}

// await trackFootfallEvent before changing href
export async function trackFootfallEventWithHrefDelay(href: string, type: EventType, data: Data = {}) {
  await trackFootfallEvent(type, data);
  window.location.href = href;
}

// Some events require certain keys, ideally these would be TS types!
// See for requirements: https://github.com/HealthUnlocked/footfall/blob/master/src/cljc/footfall/events.cljc
function isValidEvent(type: EventType, data: Data) {
  switch (type) {
    case 'clicked':
      return data.hasOwnProperty('clickType');
    case 'viewed-post':
      return data.hasOwnProperty('postId');
    case 'viewed-featured-post':
      return data.hasOwnProperty('featuredPostId');
    default:
      return true;
  }
}

function convertKeysToKebabCase(data: Data) {
  const formattedData: Data = {};
  Object.keys(data).forEach((key) => formattedData[kebabCase(key)] = data[key]);
  return formattedData;
}

function getSource() {
  const branch = getBranch() === 'B' ? ' B' : '';
  // Do not change to "Solaris" as it causes footfall error: Missing or unrecognised source
  // See get-schema-name function
  // https://github.com/HealthUnlocked/footfall/blob/321ee21a234a9ff7074c4115f9ef6e169f8a6e20/src/cljs/fooftall/core.cljs#L31-L35
  return `Betelgeuse${branch}`;
}

function getReferrer(isAnonymous: boolean) { // [CHAOS-131]
  if (isAnonymous) return '';
  if (document.referrer.includes('healthunlocked.com')) {
    const huReferrer = get(Cookie.Referrer);
    if (huReferrer || huReferrer === '') {
      return huReferrer;
    }
  }
  return document.referrer;
}

function getSessionId(isAnonymous: boolean): string {
  if (isAnonymous) return '';
  const session = getJSON(Cookie.Footfall);
  const sessionId = session?.sessionId ? session.sessionId : uuid();
  if (!session) {
    set(Cookie.Footfall, { sessionId }, {expires: 30, path: '/'});
  }
  return sessionId;
}

async function setMembershipAndDeviceIds(isAnonymous: boolean) {
  if (isAnonymous) {
    deviceId = '';
    membershipId = '';
  } else if (!membershipId || !deviceId) {
    const response = await axios.get(`${config.AURORA_API_PATH}session/info`).catch(err => log.info(err, 'Error getting session info'));
    if (response && response?.data?.meta?.session) {
      const sessionData = response.data.meta.session.split(':');
      if (Array.isArray(sessionData)) {
        deviceId = sessionData[0];
        membershipId = sessionData[1];
      }
    }
  }
}

export function clearTrackingSession() {
  membershipId = null;
  deviceId = null;
}
